// utils/exceptionUtils.ts
import { ICountEntry } from "../interfaces";
import { InventoryExceptionType } from "../utils/Enum/InventoryExceptionType";

export interface ExceptionCheckOptions {
  entries: ICountEntry[];
  newEntry: ICountEntry;
  editedIndex: number | null;
  baseFlags: Map<number, any>;
  isSerialized: boolean;
  isCartoned: boolean;
  isPalletized: boolean;
  skipInsert?: boolean;
}

const normalize = (val?: string | null) => val?.trim().toLowerCase() || "";

export function validateLocally({
  entries,
  newEntry,
  editedIndex,
  baseFlags,
  isSerialized,
  isCartoned,
  isPalletized,
  skipInsert,
}: ExceptionCheckOptions): { updatedEntries: ICountEntry[]; updatedEntry: ICountEntry } {
  const newSerial = normalize(newEntry.serialNo);
  const newCarton = normalize(newEntry.carton);
  const newPallet = normalize(newEntry.pallet);

  const revalidated = entries.map((e) => {
    const flags = e.id ? baseFlags.get(e.id) : undefined;
    return {
      ...e,
      hasUnacceptedSerialException: flags?.hasUnacceptedSerialException || false,
      hasUnacceptedCartonException: flags?.hasUnacceptedCartonException || false,
      hasUnacceptedPalletException: flags?.hasUnacceptedPalletException || false,
      localExceptions: null,
    } as ICountEntry;
  });

  let updatedEntry = { ...newEntry } as ICountEntry;
  let workingEntries: ICountEntry[] = [...revalidated];

  if (!skipInsert) {
    if (editedIndex !== null && editedIndex >= 0) {
       const {
    hasUnacceptedPalletException,
    hasUnacceptedCartonException,
    hasUnacceptedSerialException,
    localExceptions,
    ...safeFields
  } = updatedEntry;

  workingEntries[editedIndex] = {
    ...workingEntries[editedIndex],
    ...safeFields
  };
    } else {
      workingEntries.push(updatedEntry);
    }
  }

  for (let e of workingEntries) {
    // e.hasUnacceptedSerialException = false;
    // e.hasUnacceptedCartonException = false;
    // e.hasUnacceptedPalletException = false;
    e.localExceptions = null;
  }

  const applyException = (entry: ICountEntry, type: InventoryExceptionType) => {
    const current = entry.localExceptions ? entry.localExceptions.split(",").map(Number) : [];
    if (!current.includes(type)) {
      current.push(type);
      entry.localExceptions = current.join(",");
    }
  };

  if (isSerialized) {
    const serialMap = new Map<string, ICountEntry[]>();
    for (const entry of workingEntries) {
      const serial = normalize(entry.serialNo);
      if (!serial) continue;
      if (!serialMap.has(serial)) {
        serialMap.set(serial, []);
      }
      serialMap.get(serial)!.push(entry);
    }
    for (const [_, list] of Array.from(serialMap.entries())) {
      if (list.length > 1) {
        for (const entry of list) {
          if( list.filter(x=>x.hasUnacceptedSerialException === undefined || x.hasUnacceptedSerialException || (normalize(x.storedSerialNo) !== undefined && (x.hasUnacceptedSerialException === false && normalize(x.storedSerialNo) !== normalize(x.serialNo))) || x.id === undefined || x.id === 0)?.length > 0){
          entry.hasUnacceptedSerialException = true;
          }
          applyException(entry, InventoryExceptionType.DuplicateSerials);
        }
      }
    }
  }

  if (isCartoned && isPalletized) {
    const cartonToPallets = new Map<string, Set<string>>();
    const cartonToEntries = new Map<string, ICountEntry[]>();

    for (const entry of workingEntries) {
      const carton = normalize(entry.carton);
      const pallet = normalize(entry.pallet);
      if (!carton) continue;

      if (!cartonToPallets.has(carton)) {
        cartonToPallets.set(carton, new Set());
        cartonToEntries.set(carton, []);
      }

      cartonToPallets.get(carton)!.add(pallet);
      cartonToEntries.get(carton)!.push(entry);
    }

    const cartonsWithConflicts: string[] = [];

    for (const [carton, pallets] of cartonToPallets.entries()) {
      if (pallets.size > 1) {
         const similarCartons = workingEntries.filter(x=>normalize(x.carton)===normalize(carton) && (x.hasUnacceptedCartonException === undefined || x.hasUnacceptedCartonException|| (normalize(x.carton) !== undefined && (x.hasUnacceptedCartonException === false && normalize(x.storedCarton) !== normalize(x.carton))) || x.id === undefined || x.id === 0));
         if(similarCartons?.length > 0)
          for(const entry of workingEntries.filter(x=>normalize(x.carton) === normalize(carton))){
        entry.hasUnacceptedCartonException = true;
          }
        cartonsWithConflicts.push(carton);
      }
    }
    for (const entry of workingEntries) {
      const carton = normalize(entry.carton);
      const similarCarton = workingEntries.filter(x=>normalize(x.carton) === carton 
          && (x.hasUnacceptedCartonException === undefined || x.hasUnacceptedCartonException === true|| (normalize(x.storedCarton) !== undefined && (x.hasUnacceptedCartonException === false && normalize(x.storedCarton) !== normalize(x.carton))) || x.id === undefined || x.id ===0) 
          && (x.otherTagExceptions?.includes(Number(InventoryExceptionType.CartonsOnMultiplePallets).toString()) 
          || (x.localExceptions?.includes(Number(InventoryExceptionType.CartonsOnMultiplePallets).toString()))
        )
      );
      const similarCartonDifferetTag = entry.otherTagExceptions?.includes(Number(InventoryExceptionType.CartonsOnMultiplePallets).toString()) && entry.storedPallet && !entry.hasUnacceptedPalletException && entry?.pallet !== entry?.storedPallet;
      if (cartonsWithConflicts.includes(carton) ||  (similarCarton?.length > 0)) {
        if(similarCarton?.length > 0){
            entry.hasUnacceptedCartonException = true;
        }
        applyException(entry, InventoryExceptionType.CartonsOnMultiplePallets);
      }
      else if(similarCartonDifferetTag){ //handle change carton after accepted pallet change
            entry.hasUnacceptedCartonException = true;
        applyException(entry, InventoryExceptionType.CartonsOnMultiplePallets);
      }
    }

  }

  if (isPalletized) {
    const palletMap = new Map<string, ICountEntry[]>();
    for (const entry of workingEntries) {
      const pallet = normalize(entry.pallet);
      if (!pallet) continue;
      if (!palletMap.has(pallet)) {
        palletMap.set(pallet, []);
      }
      palletMap.get(pallet)!.push(entry);
    }
    for (const [_, list] of Array.from(palletMap.entries())) {
      if (
        (list.length > 1 
        || list.filter(x=>x.otherTagExceptions?.includes(Number(InventoryExceptionType.PalletsOnMultipleLocations).toString()))?.length > 0
        ) 
        && list.filter(x=>x.hasUnacceptedPalletException === undefined || x.hasUnacceptedPalletException|| (normalize(x.storedPallet) !== undefined 
        && (x.hasUnacceptedPalletException === false && normalize(x.storedPallet) !== normalize(x.pallet))) || x.id === undefined || x.id === 0)?.length > 0) {
        for (const entry of list) {
          entry.hasUnacceptedPalletException = true;
        }
      }
    }
  }

  return {
    updatedEntries: workingEntries,
    updatedEntry,
  };
}
